import type {
  ExistingInventoryItem,
  CatalogInventoryItem,
  CatalogItemVariant,
  StockStatus
} from '../Types';

import { formatUnitDefinition } from './UnitDefinition.utils';

/** Dropdown label: "Roma Tomatoes - cs - 8 kg" */
export const formatSearchLabel = (
  name: string,
  unit: string,
  unitMultiplier?: number | string | null,
  baseUnit?: string | null
): string => {
  const base = `${name.trim()} - ${unit.trim()}`;
  const multRaw =
    unitMultiplier != null && unitMultiplier !== ''
      ? Number(unitMultiplier)
      : null;
  const baseUnitTrimmed = baseUnit?.trim() ?? '';

  if (
    multRaw != null &&
    !Number.isNaN(multRaw) &&
    multRaw > 0 &&
    baseUnitTrimmed
  ) {
    const multDisplay = Number.isInteger(multRaw)
      ? String(multRaw)
      : String(multRaw);
    return `${base} - ${multDisplay} ${baseUnitTrimmed}`;
  }

  return base;
};

const defaultStockStatus = (qty: number, min: number): StockStatus => {
  if (qty <= 0) return 'OUT_OF_STOCK';
  if (min > 0 && qty <= min) return 'LOW_STOCK';
  return 'IN_STOCK';
};

/** One catalog item with N variants → N search dropdown rows */
export const expandCatalogToSearchOptions = (
  items: CatalogInventoryItem[]
): ExistingInventoryItem[] =>
  items.flatMap(item =>
    item.variants.map(variant => toSearchOption(item, variant))
  );

const toSearchOption = (
  item: CatalogInventoryItem,
  variant: CatalogItemVariant
): ExistingInventoryItem => {
  const qty = variant.quantityOnHand ?? 0;
  const min = variant.minQuantity ?? 0;

  return {
    id: `${item.id}:${variant.unit}:${variant.unitMultiplier ?? ''}:${variant.baseUnit ?? ''}`,
    catalogItemId: item.id,
    variantId: variant.variantId ?? null,
    name: item.name,
    unit: variant.unit,
    displayLabel: formatSearchLabel(
      item.name,
      variant.unit,
      variant.unitMultiplier,
      variant.baseUnit
    ),
    internalSku: item.internalSku,
    locationId: item.locationId,
    locationName: item.locationName,
    quantityOnHand: qty,
    minQuantity: min,
    stockStatus: variant.stockStatus ?? defaultStockStatus(qty, min),
    baseUnit: variant.baseUnit ?? null,
    unitMultiplier: variant.unitMultiplier ?? null,
    unitDefinition:
      variant.baseUnit && variant.unitMultiplier
        ? formatUnitDefinition(
            variant.unit,
            String(variant.unitMultiplier),
            variant.baseUnit
          )
        : null
  };
};

/** API rows are already one row per unit; ensure displayLabel is set */
export const normalizeApiSearchOptions = (
  rows: ExistingInventoryItem[]
): ExistingInventoryItem[] =>
  rows.map(row => ({
    ...row,
    displayLabel:
      row.displayLabel ||
      formatSearchLabel(row.name, row.unit, row.unitMultiplier, row.baseUnit),
    id: row.id.includes(':') ? row.id : `${row.id}:${row.unit}`
  }));

export const filterSearchOptions = (
  options: ExistingInventoryItem[],
  query: string,
  limit = 15
): ExistingInventoryItem[] => {
  const term = query.trim().toLowerCase();
  if (!term) return options.slice(0, limit);

  return options
    .filter(
      item =>
        item.name.toLowerCase().includes(term) ||
        item.displayLabel.toLowerCase().includes(term) ||
        item.unit.toLowerCase().includes(term) ||
        (item.baseUnit?.toLowerCase().includes(term) ?? false) ||
        (item.unitMultiplier != null &&
          String(item.unitMultiplier).includes(term)) ||
        (item.internalSku?.toLowerCase().includes(term) ?? false)
    )
    .slice(0, limit);
};
